import React, { useState, useEffect, useRef } from "react";
import Layout from "../components/ui/Layout";
import { Link } from "react-router-dom";
import ProgressBar from "../components/ui/ProgressBar";
import { Play, Pause } from "lucide-react";
import ScrollToTopButton from "../components/ui/ScrollToTopButton";
import InPageNav from '../components/ui/InPageNav';
import { ICArticleBody, ICH2, ICFigure } from "../components/ui/IntranetArticle";
import { IC_PERMISSION_COVER } from "../data/caseStudyMedia";
import CaseOpen from "../components/ui/CaseOpen";
import "./IntranetConnections.css";

const IntranetConnectionsCaseStudy = () => {
  const caseRef = useRef(null);
  const [zoomedImage, setZoomedImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const toggleZoom = (image) => {
    setZoomedImage(zoomedImage === image ? null : image);
  };

  useEffect(() => {
    if (zoomedImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => (document.body.style.overflow = "auto");
  }, [zoomedImage]);

  const videoRefs = {
    SecurityFlow: useRef(null),
  };

  const [isPlaying, setIsPlaying] = useState({
    SecurityFlow: true,
  });

  const [isHovered, setIsHovered] = useState({
    SecurityFlow: false,
  });

  const toggleVideoPlayback = (ref, currentlyPlaying, updateState) => {
    if (ref.current) {
      if (currentlyPlaying) {
        ref.current.pause();
        updateState(false);
      } else {
        ref.current.play();
        updateState(true);
      }
    }
  };

  const toggleVideo = (key) => {
    toggleVideoPlayback(videoRefs[key], isPlaying[key], (newState) => {
      setIsPlaying((prev) => ({ ...prev, [key]: newState }));
    });
  };

  return (
    <Layout>
      <div className="intranet-case" ref={caseRef}>
      <InPageNav />

      {/* Fullscreen Image Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black z-50"
          onClick={() => setZoomedImage(null)}
        >
          <img
            src={zoomedImage}
            alt="Zoomed View"
            className="w-auto h-auto max-w-full max-h-full object-contain cursor-zoom-out transition-transform duration-300 scale-100"
          />
        </div>
      )}

      {/* Skip to Content Link */}
      <a
        href="#overview"
        className="sr-only focus:not-sr-only focus:fixed focus:top-0 focus:left-0 focus:bg-white focus:p-4 focus:z-50 focus:text-black focus:underline"
      >
        Skip to Content
      </a>

      {!zoomedImage && <ProgressBar />}
      <ScrollToTopButton />

      <CaseOpen
        src={IC_PERMISSION_COVER}
        alt="Intranet Connections permission redesign overview"
        eyebrow="Case study · Intranet Connections"
        title="Making complex access control look simple for 500+ security-heavy orgs"
        dek="Years of layered rules had piled up into 12 roles and 20+ screens. We kept the hard thinking behind the interface: 5 roles, 2 screens, one pattern you can reuse. 50% more people used the right access."
        overviewId="overview"
        meta={[
          { label: "Role", value: "Solo product design" },
          { label: "Timeline", value: "2 months, 2024" },
          { label: "Team", value: "Product & engineering" },
          { label: "Client context", value: "500+ enterprise orgs" },
        ]}
      />

      <section className="ic-section">
        <div className="ic-figure-grid ic-figure-grid--2 ic-focus-block">
          <figure className="ic-figure">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745488313/Frame_2147223216_ltltht.png"
              alt="Legacy permission system mapping"
              onClick={() =>
                toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745488313/Frame_2147223216_ltltht.png")
              }
            />
            <figcaption>Legacy permission system</figcaption>
          </figure>
          <figure className="ic-figure">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744797898/Permission_Mapping_1_rorv1x.png"
              alt="User journey synthesis"
              onClick={() =>
                toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744797898/Permission_Mapping_1_rorv1x.png")
              }
            />
            <figcaption>User journey synthesis</figcaption>
          </figure>
        </div>
      </section>

      <section id="discovery" className="ic-section ic-section--divider">
        <ICH2>Discovery</ICH2>
        <ICArticleBody>
          <p>
            I was asked to redesign permission control for Intranet Connections, a platform used by banks, hospitals,
            and government teams. The existing model had 12+ roles, inconsistent flows, and heavy overgranting.
          </p>
          <p>I still remember my first user interview with a credit union customer at Intranet Connections (IC).</p>
          <div className="ic-quote__attribution">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1761304100/Default_vsrutv.png"
              alt=""
              className="ic-quote__avatar"
            />
            <span>Credit union customer · first interview</span>
          </div>
          <blockquote className="ic-quote ic-quote--center">&ldquo;Why did you choose our product?&rdquo;</blockquote>
          <p>
            After all, IC is a 25-year-old product. Twenty-five years ago, Yahoo was still the front door to the
            internet: blue links, black text, playful icons. Google was just getting started. I hadn&apos;t even gotten
            my first phone yet.
          </p>
          <p>
            Yet for 25 years this product has quietly powered over <strong>500 organizations</strong>, banks,
            hospitals, government teams, as their centralized intranet, information hub, and employee engagement tool.
          </p>
          <p>
            IC wasn&apos;t just old. It was layered, shaped by customer habits, workarounds, and fragmented systems
            built out of necessity over decades. The answer I got was simple, yet telling:
          </p>
          <blockquote className="ic-quote ic-quote--center">&ldquo;Security.&rdquo;</blockquote>
        </ICArticleBody>
      </section>

      <section id="discovery-problem" className="ic-section">
        <p className="ic-kicker ic-focus-block">Homepages in legacy</p>
        <div className="ic-figure-grid ic-figure-grid--2 ic-focus-block">
          <figure className="ic-figure">
            <img
              alt="Legacy client homepage"
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741778703/Group_1000003464_1_w3sfdp.png"
              onClick={() =>
                toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741778703/Group_1000003464_1_w3sfdp.png")
              }
            />
          </figure>
          <figure className="ic-figure">
            <img
              alt="Legacy client homepage variant"
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741778700/Group_1000003465_2_xqgnbn.png"
              onClick={() =>
                toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741778700/Group_1000003465_2_xqgnbn.png")
              }
            />
          </figure>
        </div>
        <ICArticleBody>
          <p>
            The importance of security only became more obvious as we worked toward <strong>SOC 2 compliance</strong>, we were serving banks, hospitals, and even <strong>NASA</strong> (yes, NASA!).
          </p>
          <p>
            So when I finally reached permission control in our product revamp, one of the most business-critical
            features, I was more than excited.
          </p>
          <blockquote className="ic-quote">
            But as a solo designer navigating a legacy system layered with technical debt, customer habits, and business
            risks, the journey was far from easy.
          </blockquote>
        </ICArticleBody>
      </section>

      <section className="ic-section">
        <ICH2>Research</ICH2>
        <ICArticleBody>
          <blockquote className="ic-quote">
            UI is not the problem. Faster design delivery wouldn&apos;t save our team development effort.
          </blockquote>
          <p>
            On the surface, I was asked to &ldquo;make the permission UI easier to use.&rdquo; I thought we could speed it
            up by swapping old UIs for framework components while rushing the first release. The business impact and
            curiosity about how people actually use permissions pushed me to ask for more research.
          </p>
        </ICArticleBody>
        <div className="ic-figure-grid ic-figure-grid--3 ic-focus-block">
          <figure className="ic-figure">
            <img
              src="https://support.intranetconnections.com/hc/article_attachments/360101441334"
              alt="Assign roles in legacy"
              onClick={() => toggleZoom("https://support.intranetconnections.com/hc/article_attachments/360101441334")}
            />
            <figcaption>Assign roles · text-heavy</figcaption>
          </figure>
          <figure className="ic-figure">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745187675/mceclip1_x7szjg.png"
              alt="Folder permission in legacy"
              onClick={() => toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745187675/mceclip1_x7szjg.png")}
            />
            <figcaption>Folder permission · low discoverability</figcaption>
          </figure>
          <figure className="ic-figure">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745478294/Screenshot_2025-04-24_at_12.03.53_AM_qv7lay.png"
              alt="Page permission in legacy"
              onClick={() =>
                toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745478294/Screenshot_2025-04-24_at_12.03.53_AM_qv7lay.png")
              }
            />
            <figcaption>Page permission · no granularity</figcaption>
          </figure>
        </div>
      </section>

      <section id="discovery-customer" className="ic-section">
        <ICArticleBody>
          <p>
            Meanwhile, through competitor research and customer feedback, it became clear: clients were asking for more
            granular access control across different types of content.
          </p>
          <div className="ic-quote__attribution">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741764333/2_z4heeg.png"
              alt=""
              className="ic-quote__avatar"
            />
            <span>Information manager · credit union</span>
          </div>
          <blockquote className="ic-quote">
            &ldquo;I don&apos;t want employees to see when others are getting physicals done. I want the company physical
            calendar open so everyone can add an event. But only HR and the employee who added it can view it.&rdquo;
          </blockquote>
          <div className="ic-quote__attribution">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1741764342/29_ewl2js.png"
              alt=""
              className="ic-quote__avatar"
            />
            <span>IT manager · healthcare</span>
          </div>
          <blockquote className="ic-quote">
            &ldquo;I would like staff with elevated rights (Super Admins) to change document owners at the folder
            level.&rdquo;
          </blockquote>
        </ICArticleBody>
      </section>

      <section id="define" className="ic-section ic-section--divider">
        <ICH2>Define</ICH2>
        <ICArticleBody>
          <p>With all research insights, the redesign needed to address three problems:</p>
          <ul className="ic-list">
            <li>
              <strong>Reduce overgranting</strong>: prevent users from receiving more access than needed.
            </li>
            <li>
              <strong>Improve granularity</strong>: fine-tuned control over content, tasks, and actions with consistent
              flows.
            </li>
            <li>
              <strong>Enhance role adoption</strong>: make the right roles easy to choose without training.
            </li>
          </ul>
          <blockquote className="ic-quote ic-quote--center">
            How might we build a safer, intuitive experience for content managers to grant the right content to the
            right people?
          </blockquote>
        </ICArticleBody>
      </section>

      <section id="ideation" className="ic-section">
        <ICH2>Ideation</ICH2>
        <ICArticleBody>
          <h3 className="ic-h3">A bold idea: &ldquo;Where they see is where they permit.&rdquo;</h3>
          <p>
            Users struggled to understand sites, apps, pages, and storyboards without heavy training. The most common
            roles were Super Admin and Admin; for everything else, it boiled down to managing information.
          </p>
          <p>
            What if we kept only Super Admin and Admin, and made all other content permission purely attribute-based
            instead of maintaining 10+ confusing roles?
          </p>
        </ICArticleBody>
        <ICFigure
src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745188933/Permission_Mapping_3_beyw22.png"
          alt="Concept sketch: role simplification"
          caption="Concept 1: Admin / Super Admin only; content permissions by attributes."
          onZoom={toggleZoom}
        />
      </section>

      <section className="ic-section ic-section--divider">
        <ICH2>Challenge</ICH2>
        <ICArticleBody>
          <p>
            The PM and I aligned on direction quickly. But stakeholders were right to worry. Customers had spent years
            building their own security models. If we removed roles and changed the flow, how would we migrate without
            losing settings?
          </p>
          <blockquote className="ic-quote">
            Thinking about the complexity was intimidating: so many hidden, nuanced settings inside permission control.
            I mapped the legacy system piece by piece to find a migration path.
          </blockquote>
        </ICArticleBody>
        <ICFigure
          src="https://images.prismic.io/thedecisionlab/0c5c5546-cea6-4621-982e-80562a800f4e_Status-Quo-Bias.jpeg?auto=compress,format"
          alt="Status quo bias"
          caption="Status quo bias: a lens for stakeholder concerns."
          onZoom={toggleZoom}
        />
        <ICFigure
src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745488313/Frame_2147223216_ltltht.png"
          alt="Legacy permission system mapping"
          caption="Legacy permission system map."
          onZoom={toggleZoom}
        />
      </section>

      <section className="ic-section">
        <ICH2>Synthesize</ICH2>
        <ICArticleBody>
          <p>
            After mapping the legacy system, the pattern was clear: apps, pages, admin center, different containers,
            same job. Information access control between a giver and an asker.
          </p>
          <p>Our service goal boiled down to three questions:</p>
          <ul className="ic-list">
            <li>
              <strong>Permission giver:</strong> How do I grant the right access in less time, with fewer mistakes?
            </li>
            <li>
              <strong>Permission asker:</strong> How do I get the access I need with less friction?
            </li>
          </ul>
          <p>
            That framing opened a path to migrate legacy settings safely while building a more scalable model.
          </p>
        </ICArticleBody>
        <ICFigure
src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744797898/Permission_Mapping_1_rorv1x.png"
          alt="Information access mapping"
          caption="Information access mapping."
          onZoom={toggleZoom}
        />
      </section>

      <section id="solution" className="ic-section ic-section--divider">
        <ICH2>Solution</ICH2>
        <ICArticleBody>
          <p className="ic-dek">
            A safer access-control model that balances existing mental models with what new users need.
          </p>
        </ICArticleBody>
        <ICFigure
src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744798147/Permission_Mapping_2_vk7f1l.png"
          alt="Permission mapping overview"
          caption="Permission mapping overview."
          onZoom={toggleZoom}
        />
        <ICArticleBody>
          <h3 className="ic-h3">1 · From 12 to 5 roles</h3>
        </ICArticleBody>
        <ICFigure
src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744800107/12-5_ah6uyu.png"
          alt="Security roles reduction"
          onZoom={toggleZoom}
        />

        <ICArticleBody>
          <h3 className="ic-h3">2 · Granular control, same expectation everywhere</h3>
          <p>Users should get the same permission patterns whether they are in apps, folders, or items.</p>
        </ICArticleBody>
        <div className="ic-figure-grid ic-figure-grid--3 ic-focus-block">
          <figure className="ic-figure">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744798531/App_Permissions_vhrqub.png"
              alt="App permissions"
              onClick={() =>
                toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744798531/App_Permissions_vhrqub.png")
              }
            />
            <figcaption>Apps</figcaption>
          </figure>
          <figure className="ic-figure">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744798527/Folder_Permissions_gtplp0.png"
              alt="Folder permissions"
              onClick={() =>
                toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744798527/Folder_Permissions_gtplp0.png")
              }
            />
            <figcaption>Folders</figcaption>
          </figure>
          <figure className="ic-figure">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744798525/Item_Permissions_ub1ykt.png"
              alt="Item permissions"
              onClick={() =>
                toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1744798525/Item_Permissions_ub1ykt.png")
              }
            />
            <figcaption>Items</figcaption>
          </figure>
        </div>

        <ICArticleBody>
          <h3 className="ic-h3">3 · Intuitive permission granting flow</h3>
        </ICArticleBody>
        <figure className="ic-figure ic-focus-block">
          <div
            className="relative"
            onMouseEnter={() => setIsHovered((prev) => ({ ...prev, SecurityFlow: true }))}
            onMouseLeave={() => setIsHovered((prev) => ({ ...prev, SecurityFlow: false }))}
          >
            <video
              ref={videoRefs.SecurityFlow}
              src="https://res.cloudinary.com/dlzvz9qj8/video/upload/v1745492677/Screen_Recording_2025-04-24_at_4.03.48_AM_jx13ja.mov"
              className="w-full"
              autoPlay
              loop
              muted
              playsInline
              aria-label="Security flow screen recording"
            />
            {isHovered.SecurityFlow && (
              <button
                type="button"
                onClick={() => toggleVideo("SecurityFlow")}
                className="absolute bottom-4 left-4 text-white bg-black/50 p-3 rounded-full"
                aria-label="Toggle video play/pause"
              >
                {isPlaying.SecurityFlow ? <Pause size={24} /> : <Play size={24} />}
              </button>
            )}
          </div>
          <figcaption>Permission granting flow.</figcaption>
        </figure>

        <ICArticleBody>
          <h3 className="ic-h3">4 · Ask-for-access pattern</h3>
        </ICArticleBody>
        <div className="ic-figure-grid ic-figure-grid--2 ic-focus-block">
          <figure className="ic-figure">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1761298416/Edit_affqes.png"
              alt="Request for access"
              onClick={() =>
                toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745494501/with_permission_hagnu6.png")
              }
            />
          </figure>
          <figure className="ic-figure">
            <img
              src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745994944/with_permission_ved91i.png"
              alt="Request for access approved"
              onClick={() =>
                toggleZoom("https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745994944/with_permission_ved91i.png")
              }
            />
          </figure>
        </div>
      </section>

      <section id="test-iterations" className="ic-section ic-section--divider">
        <ICH2>Test and iterations</ICH2>
        <ICArticleBody>
          <p>
            I didn&apos;t run customer tests before ship. But I ran quick internal sessions, gathered feedback across
            departments, and iterated on visual cues, safer admin selection, and coverage for edge cases.
          </p>
          <h3 className="ic-h3">Visual cues for updated security</h3>
        </ICArticleBody>
        <ICFigure
src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745496263/Frame_2147223229_uvaryv.png"
          alt="Visual cues iteration"
          onZoom={toggleZoom}
        />
        <ICArticleBody>
          <h3 className="ic-h3">Safer role permission</h3>
        </ICArticleBody>
        <ICFigure
src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745535493/Frame_2147223232_tlrqsl.png"
          alt="Safer role permission"
          onZoom={toggleZoom}
        />
        <ICArticleBody>
          <h3 className="ic-h3">Permission on dependent content</h3>
        </ICArticleBody>
        <ICFigure
src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745536882/Frame_2147223233_1_hufj4m.png"
          alt="Connected permission control"
          onZoom={toggleZoom}
        />
      </section>

      <section id="outcome" className="ic-section ic-section--divider">
        <ICH2>The outcome</ICH2>
        <ICArticleBody>
          <p>
            The design shipped in the new product with 100% task completion in internal testing, and it met every
            design goal we set at the start.
          </p>
        </ICArticleBody>
        <div className="ic-stats ic-focus-block">
          <div>
            <span className="ic-stats__value">+50%</span>
            <span className="ic-stats__label">Role adoption across users</span>
          </div>
          <div>
            <span className="ic-stats__value">−15%</span>
            <span className="ic-stats__label">Overgranting of permissions</span>
          </div>
          <div>
            <span className="ic-stats__value">Granular</span>
            <span className="ic-stats__label">Control for items and apps</span>
          </div>
        </div>
      </section>

      {/* Moving Forward */}
      <section id="moving-forward" className="ic-section ic-section--divider">
        <ICH2>Moving forward</ICH2>
        <ICArticleBody>
          <p>
            After the first release, we will collect live customer feedback and test data migration, plus third-party
            vulnerability testing. I&apos;m curious about market sentiment on the new security architecture and aware of
            unmet needs around permission auditing. Never ends.
          </p>
        </ICArticleBody>
      </section>

      <section id="reflection" className="ic-section">
        <ICH2>System thinking in action</ICH2>
        <ICArticleBody>
          <p>
            From ambiguity to clarity, it felt like sorting out an entangled ball. System thinking is what made the
            solution shippable: how parts connect, interoperate, and chain together across the platform.
          </p>
        </ICArticleBody>
      </section>

      {/* Case Study Navigation */}
      <section className="ic-section ic-section--divider">
        <ICArticleBody className="ic-article__footer" focus={false}>
          <Link to="/Collabstr" className="ic-nav-link">
            ← Collabstr
          </Link>
          <Link to="/DesignSystem" className="ic-nav-link">
            Design System →
          </Link>
        </ICArticleBody>
      </section>
      </div>
    </Layout>
  );
};

export default IntranetConnectionsCaseStudy;
